/**
 * Flota — zestaw komponentów wizualnych (`fleet.web.kit.*`, patrz `features/fleet/ui/README.md`), doklejony w `extra.ts`.
 * `fleetKitEn` jest typowany względem `fleetKitPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetKitPl = {
  kit: {
    dayState: { ON_ROUTE: 'W trasie', PARKED: 'Postój', IN_SERVICE: 'W serwisie' },
    plateAria: 'Numer rejestracyjny {plate}',
    health: {
      title: 'Zdrowie',
      aria: '{label}: {score} na 100, {band}',
      noScore: 'brak danych',
      band: { good: 'Dobre', attention: 'Wymaga uwagi', critical: 'Krytyczne' },
      penalty: {
        dueOverdue: 'Termin po czasie',
        dueCritical: 'Termin w ciągu 7 dni',
        dueSoon: 'Termin w ciągu 30 dni',
        incidentSerious: 'Poważne zgłoszenie',
        incident: 'Otwarte zgłoszenie',
        leaseOver: 'Limit leasingu przekroczony',
        leaseHigh: 'Limit leasingu ≥ 90%',
        leaseWarn: 'Limit leasingu ≥ 80%',
        leaseForecast: 'Prognoza przekroczenia limitu',
        missingLogbook: 'Brak karty drogowej',
        elevatedCost: 'Podwyższone koszty',
      },
    },
    deadline: {
      overdue: 'po terminie o {n} dni',
      overdueOne: 'po terminie o 1 dzień',
      today: 'dziś',
      tomorrow: 'jutro',
      left: 'za {n} dni',
      none: 'brak terminu',
      kind: { INSPECTION: 'Przegląd', INSURANCE: 'Ubezpieczenie' },
    },
    gauge: {
      used: '{percent} limitu',
      forecast: 'Prognoza na koniec umowy: {percent}',
      forecastIndicative: 'Prognoza orientacyjna: {percent}',
      limit: 'Limit',
      aria: 'Wykorzystanie limitu: {percent}',
      thresholds: 'Progi ostrzegawcze {list}',
    },
    trend: { up: 'wzrost', down: 'spadek', flat: 'bez zmian', vsPrev: 'vs poprzedni okres' },
    view: { label: 'Widok', table: 'Tabela', cards: 'Karty' },
    filters: { label: 'Filtry', clear: 'Wyczyść filtry' },
    empty: { title: 'Brak danych', description: 'Zmień filtry albo wróć później.' },
    loading: 'Wczytywanie…',
    timeline: { empty: 'Brak zdarzeń.' },
    kv: { empty: 'brak' },
  },
};

export const fleetKitEn: Shape<typeof fleetKitPl> = {
  kit: {
    dayState: { ON_ROUTE: 'On route', PARKED: 'Parked', IN_SERVICE: 'In service' },
    plateAria: 'Registration plate {plate}',
    health: {
      title: 'Health',
      aria: '{label}: {score} out of 100, {band}',
      noScore: 'no data',
      band: { good: 'Good', attention: 'Needs attention', critical: 'Critical' },
      penalty: {
        dueOverdue: 'Deadline overdue',
        dueCritical: 'Deadline within 7 days',
        dueSoon: 'Deadline within 30 days',
        incidentSerious: 'Serious incident',
        incident: 'Open incident',
        leaseOver: 'Lease limit exceeded',
        leaseHigh: 'Lease limit ≥ 90%',
        leaseWarn: 'Lease limit ≥ 80%',
        leaseForecast: 'Forecast to exceed limit',
        missingLogbook: 'Missing logbook',
        elevatedCost: 'Elevated costs',
      },
    },
    deadline: {
      overdue: '{n} days overdue',
      overdueOne: '1 day overdue',
      today: 'today',
      tomorrow: 'tomorrow',
      left: 'in {n} days',
      none: 'no deadline',
      kind: { INSPECTION: 'Inspection', INSURANCE: 'Insurance' },
    },
    gauge: {
      used: '{percent} of limit',
      forecast: 'End-of-contract forecast: {percent}',
      forecastIndicative: 'Indicative forecast: {percent}',
      limit: 'Limit',
      aria: 'Limit usage: {percent}',
      thresholds: 'Warning thresholds {list}',
    },
    trend: { up: 'up', down: 'down', flat: 'no change', vsPrev: 'vs previous period' },
    view: { label: 'View', table: 'Table', cards: 'Cards' },
    filters: { label: 'Filters', clear: 'Clear filters' },
    empty: { title: 'No data', description: 'Change the filters or check back later.' },
    loading: 'Loading…',
    timeline: { empty: 'No events.' },
    kv: { empty: 'none' },
  },
};
